import {Color} from '@benchling/nucleus-ui';
import {Text, Center, Container} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import reactUpdate from 'immutability-helper';
import React, {memo, useRef, useCallback} from 'react';
import {v4 as uuidv4} from 'uuid';

import {parseReactionComponents} from 'chemistry/ChemistryDAO';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import ReactionSchemeContent from 'notebook/integrations/chemistry/ReactionSchemeContent';
import ReactionSketcherModal from 'notebook/integrations/chemistry/ReactionSketcherModal';
import {
  StoichiometryStructuredTableRowMetadata,
  StoichiometryStructuredTable,
  StoichiometryTableCustomSlotProps,
} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableColumnGroups,
} from 'notebook/structured-tables/StructuredTableConstants';
import getBlankRow from 'notebook/structured-tables/util/getBlankRow';
import {BaseTypedValueType} from 'notebook/tables/TableConstants';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {withLoadingScreen} from 'util/Promises';

async function handleReactionSketcherSubmit(
  mrvStructure: string,
  folderId: string,
  structuredTable: StoichiometryStructuredTable,
  updateHandler: (structuredTable: StoichiometryStructuredTable) => void
): Promise<void> {
  // Parse the reaction into its components.
  const {externalFile, reactants, products} = await parseReactionComponents(mrvStructure, folderId);
  const compoundColumnIndex = structuredTable.columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_MOLECULE
  );
  const roleColumnIndex = structuredTable.columns.findIndex(
    (col) => col.columnGroup === StructuredTableColumnGroups.CHEMISTRY_REACTION_ROLE
  );

  if (compoundColumnIndex === -1 || roleColumnIndex === -1) {
    throw new Error('Could not find required columns');
  }

  const reactantRows = createRowsForReactionComponent(
    reactants,
    ChemistryReactionRole.REACTANT,
    compoundColumnIndex,
    roleColumnIndex,
    structuredTable.columns,
    0
  );

  const productRows = createRowsForReactionComponent(
    products,
    ChemistryReactionRole.PRODUCT,
    compoundColumnIndex,
    roleColumnIndex,
    structuredTable.columns,
    reactantRows.length
  );

  // Overwrite the table rows and store the external file reference in metadata.
  updateHandler({
    ...structuredTable,
    metadata: {
      ...structuredTable.metadata,
      reactionMrvExternalFile: {
        id: externalFile.id,
        url: externalFile.url,
      },
    },
    rows: [...reactantRows, ...productRows],
  });
}

function createRowsForReactionComponent(
  components: Array<{moleculeId: string; name: string}>,
  role: ChemistryReactionRole,
  nameColumnIndex: number,
  roleColumnIndex: number,
  columns: Array<StructuredTableColumn>,
  startRowIndex: number
): Array<StructuredTableRow<StoichiometryStructuredTableRowMetadata>> {
  const rows: Array<StructuredTableRow<StoichiometryStructuredTableRowMetadata>> = [];
  let rowIndex = startRowIndex;

  for (const component of components) {
    const blankCells = getBlankRow(rowIndex, columns, null);
    const cells = reactUpdate(blankCells, {
      [nameColumnIndex]: {
        text: {$set: component.name},
        typedValue: {
          $set: {
            type: BaseTypedValueType.BIOENTITY,
            bioentitySummary: {id: component.moleculeId, name: component.name},
          },
        },
      },
      [roleColumnIndex]: {
        text: {$set: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
        typedValue: {
          $set: {type: BaseTypedValueType.STRING, rawValue: CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role]},
        },
      },
    });

    rows.push({
      cells,
      rowId: uuidv4(),
      isFinalized: false,
      metadata: {},
      lastSyncedUpstreamValues: null,
    });
    rowIndex++;
  }

  return rows;
}

/**
 * A chemical sketcher rendered inside a Stoichiometry Structured Table. It can be used to populate the
 * table with reactants and products.
 */
function StoichiometryTableEmbeddedChemicalSketcher(props: StoichiometryTableCustomSlotProps): JSX.Element {
  // The sketcher is always expected to be rendered within an Entry so fileId should not be null
  const entryId = props.structuredTable.fileId!;
  const [isSketcherModalOpen, {open: openSketcherModal, close: closeSketcherModal}] = useDisclosure(false);
  const initialMarvinStructureRef = useRef<ChemicalStructureDrawerValue>(null);
  const reactionMrvExternalFile = props.structuredTable.metadata.reactionMrvExternalFile;
  const openSketcherModalWithStructure = useCallback(
    (marvinStructure: ChemicalStructureDrawerValue) => {
      initialMarvinStructureRef.current = marvinStructure;
      openSketcherModal();
    },
    [openSketcherModal]
  );
  const entryFolderId = useStoreSelector(
    EntryStore,
    useCallback(
      (entryStore: EntryStore): string | undefined =>
        entryStore.getEditorState(entryId)?.entry?.folder?.api_identifier,
      [entryId]
    )
  );

  return (
    <>
      {isSketcherModalOpen && (
        <ReactionSketcherModal
          initialMarvinStructure={initialMarvinStructureRef.current}
          onClose={closeSketcherModal}
          onSubmit={async (newMarvinStructure: string): Promise<void> => {
            closeSketcherModal();
            await withLoadingScreen(
              handleReactionSketcherSubmit(
                newMarvinStructure,
                entryFolderId!,
                props.structuredTable,
                props.updateHandler
              )
            );
          }}
        />
      )}

      <Container
        fluid={true}
        h="150px"
        p="12px"
        onClick={() => {
          // Note: this behvaior is kept for now but:
          // - there is no pointer cursor shown for the empty state
          // - it may be replaced by triggering the drag-and-drop system upload
          openSketcherModalWithStructure(null);
        }}
        style={{
          border: `1px solid ${Color.GRAY_250}`,
          borderTop: 'none',
        }}
      >
        {reactionMrvExternalFile != null ? (
          <ReactionSchemeContent
            externalFileId={reactionMrvExternalFile.id}
            externalFileUrl={reactionMrvExternalFile.url}
            entryId={entryId}
            openSketcherModalWithStructure={openSketcherModalWithStructure}
          />
        ) : (
          <Center h="100%">
            <BenchlingIcon
              icon="molecule"
              style={{width: '24px', height: '24px', color: Color.GRAY_600}}
              altText="Molecule"
            />
            <Text size="xl" color="dimmed">
              Click to start sketching
            </Text>
          </Center>
        )}
      </Container>
    </>
  );
}

export default memo(StoichiometryTableEmbeddedChemicalSketcher);
